import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { errorMessage } from '@/api/errors';
import { unwrap } from '@/api/http';
import type { Result } from '@/api/generated/types';
import { toast } from '@/ui/toast';

/** useQuery over a Result<T> endpoint: data is the unwrapped payload; BizError on code != 0. */
export function useResult<T>(key: QueryKey, fn: () => Promise<Result<T>>, enabled = true) {
  return useQuery({ queryKey: key, queryFn: async () => unwrap(await fn()), enabled });
}

/**
 * useMutation over a Result<T> endpoint with consistent UX: success toast, error toast,
 * and invalidation of the given query keys.
 */
export function useResultMutation<V, T>(
  fn: (vars: V) => Promise<Result<T>>,
  opts: { success?: string; invalidate?: QueryKey[]; onSuccess?: (data: T, vars: V) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => unwrap(await fn(vars)),
    onSuccess: (data, vars) => {
      if (opts.success) toast.success(opts.success);
      opts.invalidate?.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      opts.onSuccess?.(data, vars);
    },
    onError: (err) => {
      toast.error(errorMessage(err));
    },
  });
}
